import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { EMPTY_FILTERS, bodyToFilters, filtersToBody } from "@/components/ripeness/SavedViewsBar";

/** Filter state + the list/facet queries it drives. One body feeds both
 *  `POST /tracks/query` and `POST /facets/query`, so chip counts are always
 *  relative to the current selection (each family self-excluded). */
export function useRipenessFilters(limit = 100) {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [activeView, setActiveView] = useState(null);
    const [facets, setFacets] = useState(null);
    const [tracks, setTracks] = useState([]);
    const [totalTracks, setTotalTracks] = useState(0);
    const [valueTotal, setValueTotal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [assumptionsVersion, setAssumptionsVersion] = useState(0);

    const queryBody = useMemo(() => filtersToBody(filters, limit), [filters, limit]);

    const loadFacets = useCallback(async () => {
        try {
            const r = await api.post("/v1/ripeness/facets/query", queryBody);
            setFacets(r.data);
        } catch (e) {
            toast.error(`Facets failed: ${formatApiErrorDetail(e)}`);
        }
    }, [queryBody]);

    const loadTracks = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.post("/v1/ripeness/tracks/query", queryBody);
            setTracks(r.data.items || []);
            setTotalTracks(r.data.total || 0);
            setValueTotal(r.data.value_total || null);
        } catch (e) {
            toast.error(`Tracks load failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setLoading(false);
        }
        // assumptionsVersion forces a reload after the value assumptions were edited
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryBody, assumptionsVersion]);

    const refresh = useCallback(() => Promise.all([loadFacets(), loadTracks()]), [loadFacets, loadTracks]);

    useEffect(() => { refresh(); }, [refresh]);

    const setField = useCallback((key, value) => setFilters((f) => ({ ...f, [key]: value })), []);
    const setChips = useCallback((family) => (values) => setFilters((f) => ({ ...f, [family]: values })), []);
    const applyView = useCallback((view) => { setActiveView(view); setFilters(bodyToFilters(view.body)); }, []);
    const clearView = useCallback(() => { setActiveView(null); setFilters((f) => ({ ...f, extra: {} })); }, []);
    const assumptionsChanged = useCallback(() => setAssumptionsVersion((v) => v + 1), []);

    const tierTotals = useMemo(() => {
        const t = { hot: 0, warm: 0, watch: 0, cold: 0 };
        (facets?.tiers || []).forEach((row) => { if (row.value in t) t[row.value] = row.count; });
        return t;
    }, [facets]);

    return {
        filters, setField, setChips, activeView, applyView, clearView,
        queryBody, facets, tracks, totalTracks, valueTotal, loading, tierTotals, refresh, assumptionsChanged,
    };
}
